import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * The simple quote form (sections/wld-contact-form.liquid).
 *
 * «Lägg till i offertförfrågan» (product page) and the card's «+» link to the quote page with
 * `?product=<handle>`. This element shows that product at the top of the form and fills the two
 * hidden fields (titles, links) that carry it to the salesperson in the notification email.
 * The row is rendered in Liquid by section `wld-quote-suggestions` through the Section Rendering API
 * on the search route, so the price is the theme's — this module never formats money.
 * Removing the product empties the fields and drops `product` from the address, so a reload does
 * not bring it back. Without JavaScript the form works as before, without the product.
 */
const SUGGESTIONS_SECTION = 'wld-quote-suggestions';
/** sessionStorage key: signature of a cart whose list the visitor removed in this session. */
const CART_DISMISSED_KEY = 'wld-quote-cart-dismissed';

class WldContactForm extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {CartLine[]} */
  #cartLines = [];

  /** @type {Record<string, string>} */
  #strings = {};

  connectedCallback() {
    super.connectedCallback();
    if (window.Shopify?.designMode) return;
    this.#init();
  }

  async #init() {
    const handles = (new URLSearchParams(window.location.search).get('product') || '')
      .split(/[\s,]+/)
      .map((handle) => handle.trim())
      .filter(Boolean)
      .slice(0, 12);

    // The cart first, so a product that is both in the cart and in `?product=` is listed once.
    const cartHandles = await this.#initCart();

    if (!this.querySelector('[data-quote-product]')) return;
    this.querySelector('[data-quote-product-rows]')?.addEventListener('click', this.#handleRemove);
    const productHandles = handles.filter((handle) => !cartHandles.has(handle.toLowerCase()));
    if (productHandles.length) this.#render(productHandles);
  }

  /**
   * Fetches the cart and shows its lines. Returns the handles shown in the cart list.
   * @returns {Promise<Set<string>>}
   */
  async #initCart() {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-quote-cart]'));
    if (!box) return new Set();

    try {
      this.#strings = JSON.parse(box.querySelector('[data-quote-cart-strings]')?.textContent || '{}');
    } catch {
      this.#strings = {};
    }

    /** @type {{ items?: CartLine[], currency?: string } | null} */
    let cart = null;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      cart = await response.json();
    } catch (error) {
      console.warn('[wld-contact-form] could not read the cart', error);
      return new Set();
    }

    const items = (cart?.items || []).filter((item) => item && item.quantity > 0);
    if (!items.length) return new Set();
    if (cart?.currency) box.dataset.currency = cart.currency;

    const signature = items.map((item) => `${item.key}:${item.quantity}`).join('|');
    box.dataset.signature = signature;
    try {
      if (window.sessionStorage.getItem(CART_DISMISSED_KEY) === signature) return new Set();
    } catch {
      // sessionStorage unavailable (privacy mode): the list simply shows again
    }

    this.#cartLines = items.slice(0, 50).map((item) => ({ ...item }));
    box.querySelector('[data-quote-cart-rows]')?.addEventListener('click', this.#handleCartRemove);
    box.querySelector('[data-quote-cart-rows]')?.addEventListener('change', this.#handleCartQuantity);
    box.querySelector('[data-quote-cart-rows]')?.addEventListener('keydown', this.#handleCartKeydown);
    box.querySelector('[data-quote-cart-clear]')?.addEventListener('click', this.#handleCartClear);
    this.#renderCart();
    return new Set(this.#cartLines.map((line) => (line.handle || '').toLowerCase()));
  }

  /** @param {number} cents */
  #money(cents) {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-quote-cart]'));
    const format = box?.dataset.moneyFormat || '{{amount}}';
    const currency = box?.dataset.currency || 'SEK';
    return formatMoney(Math.round(cents), format, currency);
  }

  /**
   * @param {string} key
   * @param {string} [product]
   */
  #t(key, product = '') {
    return (this.#strings[key] || '').replace('[product]', product);
  }

  /** @param {CartLine} line */
  #variantLabel(line) {
    return !line.product_has_only_default_variant && line.variant_title ? line.variant_title : '';
  }

  /** @param {CartLine} line */
  #lineUrl(line) {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-quote-cart]'));
    try {
      return new URL(line.url, box?.dataset.shopUrl || window.location.origin).href;
    } catch {
      return line.url;
    }
  }

  #renderCart() {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-quote-cart]'));
    const rows = box?.querySelector('[data-quote-cart-rows]');
    const template = /** @type {HTMLTemplateElement | null} */ (box?.querySelector('template[data-quote-cart-row]') ?? null);
    if (!box || !rows || !template) return;

    const lines = this.#cartLines;
    const formId = this.querySelector('form')?.id || 'WldContactForm';

    rows.replaceChildren(
      ...lines.map((line, index) => {
        const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
        const item = /** @type {HTMLElement} */ (fragment.querySelector('[data-quote-cart-line]'));
        item.dataset.key = line.key;

        const media = item.querySelector('[data-cart-media]');
        if (media && line.image) {
          const img = document.createElement('img');
          const src = new URL(line.image, window.location.origin);
          src.searchParams.set('width', '104');
          img.src = src.href;
          img.alt = '';
          img.width = 52;
          img.height = 52;
          img.loading = 'lazy';
          media.append(img);
        }

        const link = /** @type {HTMLAnchorElement | null} */ (item.querySelector('[data-cart-link]'));
        if (link) {
          link.href = line.url;
          link.textContent = line.product_title;
        }

        const sub = item.querySelector('[data-cart-sub]');
        if (sub) {
          const variant = this.#variantLabel(line);
          sub.textContent = [
            variant,
            line.sku ? `${this.#t('sku')} ${line.sku}` : '',
            `${this.#money(line.final_price)} / ${this.#t('pieces')}`,
          ]
            .filter(Boolean)
            .join(' · ');
        }

        const inputId = `${formId}-cart-qty-${index}`;
        const input = /** @type {HTMLInputElement | null} */ (item.querySelector('[data-cart-qty]'));
        const label = /** @type {HTMLLabelElement | null} */ (item.querySelector('[data-cart-qty-label]'));
        if (input) {
          input.id = inputId;
          input.value = String(line.quantity);
        }
        if (label) {
          label.htmlFor = inputId;
          label.textContent = this.#t('quantityFor', line.product_title);
        }

        const price = item.querySelector('[data-cart-price]');
        if (price) price.textContent = this.#money(line.final_price * line.quantity);

        item.querySelector('[data-cart-remove]')?.setAttribute('aria-label', this.#t('remove', line.product_title));
        return fragment;
      })
    );

    this.#updateCartTotals();
    box.hidden = lines.length === 0;
  }

  /** Total and the plain-text field, after a render or a quantity change. */
  #updateCartTotals() {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-quote-cart]'));
    if (!box) return;
    const lines = this.#cartLines;
    const total = lines.reduce((sum, line) => sum + line.final_price * line.quantity, 0);
    const pieces = lines.reduce((sum, line) => sum + line.quantity, 0);

    const totalNode = box.querySelector('[data-quote-cart-total]');
    if (totalNode) totalNode.textContent = this.#money(total);

    const field = /** @type {HTMLTextAreaElement | null} */ (box.querySelector('[data-quote-cart-field]'));
    if (!field) return;
    if (!lines.length) {
      field.value = '';
      field.disabled = true;
      return;
    }

    const text = lines
      .map((line, index) => {
        const variant = this.#variantLabel(line);
        return [
          `${index + 1}. ${line.product_title}`,
          variant ? `   ${this.#t('variant')}: ${variant}` : '',
          line.sku ? `   ${this.#t('sku')}: ${line.sku}` : '',
          `   ${this.#t('quantity')}: ${line.quantity} ${this.#t('each')} ${this.#money(line.final_price)} = ${this.#money(
            line.final_price * line.quantity
          )} (${this.#t('exclVat')})`,
          `   ${this.#lineUrl(line)}`,
        ]
          .filter(Boolean)
          .join('\n');
      })
      .join('\n\n');

    field.value = `${text}\n\n${this.#t('total')}: ${pieces} ${this.#t('pieces')} · ${this.#money(total)} ${this.#t('exclVat')}`;
    field.disabled = false;
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[data-quote-cart-status]');
    if (!status) return;
    status.textContent = '';
    window.setTimeout(() => {
      status.textContent = message;
    }, 50);
  }

  /** @param {Event} event */
  #handleCartQuantity = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.matches('[data-cart-qty]')) return;
    const key = /** @type {HTMLElement | null} */ (input.closest('[data-quote-cart-line]'))?.dataset.key;
    const line = this.#cartLines.find((entry) => entry.key === key);
    if (!line) return;

    const quantity = Math.min(9999, Math.max(1, Math.floor(Number(input.value) || 1)));
    input.value = String(quantity);
    line.quantity = quantity;
    const price = input.closest('[data-quote-cart-line]')?.querySelector('[data-cart-price]');
    if (price) price.textContent = this.#money(line.final_price * quantity);
    this.#updateCartTotals();
  };

  /** Enter in a quantity commits it instead of submitting the form. @param {KeyboardEvent} event */
  #handleCartKeydown = (event) => {
    const input = /** @type {HTMLElement} */ (event.target);
    if (event.key !== 'Enter' || !input.matches('[data-cart-qty]')) return;
    event.preventDefault();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /** @param {MouseEvent} event */
  #handleCartRemove = (event) => {
    const button = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (event.target).closest('[data-cart-remove]'));
    const row = /** @type {HTMLElement | null} */ (button?.closest('[data-quote-cart-line]'));
    if (!row) return;

    const index = this.#cartLines.findIndex((line) => line.key === row.dataset.key);
    if (index < 0) return;
    const [removed] = this.#cartLines.splice(index, 1);
    if (removed && !this.#cartLines.some((line) => line.handle === removed.handle)) {
      this.#dropFromAddress([removed.handle]);
    }
    if (!this.#cartLines.length) {
      this.#dismissCart();
      return;
    }
    this.#renderCart();
    this.#announce(this.#t('removed', removed?.product_title || ''));
    const rows = [...this.querySelectorAll('[data-quote-cart-rows] [data-cart-remove]')];
    /** @type {HTMLElement | undefined} */ (rows[Math.min(index, rows.length - 1)])?.focus();
  };

  #handleCartClear = () => {
    this.#dropFromAddress(this.#cartLines.map((line) => line.handle));
    this.#dismissCart();
  };

  /**
   * A product removed from the cart list is also dropped from `?product=`, so it does not come
   * back in «Produkt i din förfrågan» on a reload.
   * @param {string[]} handles
   */
  #dropFromAddress(handles) {
    const drop = new Set(handles.map((handle) => (handle || '').toLowerCase()));
    const current = new URLSearchParams(window.location.search).get('product');
    if (!current) return;
    const keep = current
      .split(/[\s,]+/)
      .map((handle) => handle.trim())
      .filter((handle) => handle && !drop.has(handle.toLowerCase()));
    const url = new URL(window.location.href);
    if (keep.length) url.searchParams.set('product', keep.join(','));
    else url.searchParams.delete('product');
    window.history.replaceState(window.history.state, '', url);
  }

  /** Empties the list, remembers it for this cart, and moves focus to the first field. */
  #dismissCart() {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-quote-cart]'));
    this.#cartLines = [];
    this.#renderCart();
    try {
      if (box?.dataset.signature) window.sessionStorage.setItem(CART_DISMISSED_KEY, box.dataset.signature);
    } catch {
      // ignore
    }
    this.#announce(this.#t('cleared'));
    /** @type {HTMLElement | null} */ (
      this.querySelector('[data-remove-suggestion]') ?? this.querySelector('.wld-form-field__input')
    )?.focus();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /** @param {string[]} handles */
  async #render(handles) {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-quote-product]'));
    const rows = this.querySelector('[data-quote-product-rows]');
    if (!box || !rows) return;

    this.#abort?.abort();
    const abort = new AbortController();
    this.#abort = abort;

    /** @type {Element | null} */
    let rendered = null;
    if (handles.length) {
      const url = new URL(this.dataset.suggestionsUrl || '/search', window.location.origin);
      url.searchParams.set('q', handles.join(' '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'none');
      url.searchParams.set('section_id', SUGGESTIONS_SECTION);
      try {
        const response = await fetch(url, { signal: abort.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        rendered = doc.querySelector('[data-quote-suggestions]');
        this.#adoptStyles(doc);
      } catch (error) {
        if (abort.signal.aborted) return;
        console.warn('[wld-contact-form] could not show the product', error);
      }
    }
    if (abort !== this.#abort) return;

    const items = /** @type {HTMLElement[]} */ ([...(rendered?.querySelectorAll('[data-suggestion]') ?? [])]);
    rows.replaceChildren(...(rendered && items.length ? [document.importNode(rendered, true)] : []));
    box.hidden = items.length === 0;

    this.#setField('titles', items.map((item) => item.dataset.title || '').join('\n'));
    this.#setField('links', items.map((item) => item.dataset.url || '').join('\n'));
  }

  /** @param {MouseEvent} event */
  #handleRemove = (event) => {
    const button = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (event.target).closest('[data-remove-suggestion]'));
    const row = /** @type {HTMLElement | null} */ (button?.closest('[data-suggestion]'));
    if (!row) return;

    const remaining = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-quote-product-rows] [data-suggestion]')])
      .filter((item) => item !== row)
      .map((item) => item.dataset.handle || '')
      .filter(Boolean);

    // The address keeps handles that are shown in the cart list instead of here.
    const removedHandle = (row.dataset.handle || '').toLowerCase();
    const inAddress = (new URLSearchParams(window.location.search).get('product') || '')
      .split(/[\s,]+/)
      .map((handle) => handle.trim())
      .filter((handle) => handle && handle.toLowerCase() !== removedHandle);
    const url = new URL(window.location.href);
    if (inAddress.length) url.searchParams.set('product', inAddress.join(','));
    else url.searchParams.delete('product');
    window.history.replaceState(window.history.state, '', url);

    this.#render(remaining).then(() => {
      const target = /** @type {HTMLElement | null} */ (
        this.querySelector('[data-remove-suggestion]') ?? this.querySelector('.wld-form-field__input')
      );
      target?.focus();
    });
  };

  /**
   * Stylesheet subsetting: the fetched section's CSS arrives as `<style data-section-stylesheet>`
   * in the response and is not on this page — keep one copy of it.
   * @param {Document} doc
   */
  #adoptStyles(doc) {
    const style = doc.querySelector('style[data-section-stylesheet]');
    if (!style || document.querySelector(`style[data-adopted="${SUGGESTIONS_SECTION}"]`)) return;
    const copy = /** @type {HTMLStyleElement} */ (document.importNode(style, true));
    copy.dataset.adopted = SUGGESTIONS_SECTION;
    document.head.append(copy);
  }

  /**
   * @param {'titles' | 'links'} key
   * @param {string} value
   */
  #setField(key, value) {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector(`[data-product-field="${key}"]`));
    if (!input) return;
    input.value = value;
    input.disabled = !value;
  }
}

if (!customElements.get('wld-contact-form')) {
  customElements.define('wld-contact-form', WldContactForm);
}
